import type { WorkspaceRole } from "@/definitions/types";
import { WORKSPACE_ROLE_LABELS } from "@/definitions/types";

/**
 * 읽기 전용(쓰기 권한 없음) 안내 문구의 단일 출처(81차 M12 · RG81-04).
 *
 * 예전에는 화면마다 문장을 따로 조립해 한 화면 안에서도 「담당자에게」와 「소유자에게」가 섞였다.
 * 요청 대상은 **소유자**로 통일한다 — 서버(`PATCH /workspaces/{id}/members/{memberId}/role`)는 소유자 ·
 * 인사 담당자 모두 역할을 바꿀 수 있지만, 소유자가 관여하는 변경(소유자 지정 · 소유자의 역할 변경 · 제거)은
 * 소유자만 할 수 있고 소유자는 워크스페이스에 반드시 한 명 이상 있다. 그래서 「소유자에게」는 항상 맞는
 * 말이고, 「담당자」는 누구를 가리키는지 모호하다.
 *
 * 영문 역할 코드는 화면에 내지 않는다 — 항상 `WORKSPACE_ROLE_LABELS` 로 조립한다.
 */

/** 쓰기가 가능한 역할 이름(「소유자 · 인사 담당자」). */
export const WRITE_ROLES_LABEL = `${WORKSPACE_ROLE_LABELS.OWNER} · ${WORKSPACE_ROLE_LABELS.HR_ADMIN}`;

/** 권한을 바꿔 달라고 요청할 대상(「워크스페이스 소유자」). */
export const ROLE_REQUEST_TARGET = `워크스페이스 ${WORKSPACE_ROLE_LABELS.OWNER}`;

/** 요청 문장(「워크스페이스 소유자에게 요청해 주세요.」). */
export const ROLE_REQUEST_SENTENCE = `${ROLE_REQUEST_TARGET}에게 요청해 주세요.`;

/**
 * 읽기 전용 사유 한 문장 — 화면에는 한 번(페이지 자리)만 낸다. 버튼 아래 · 카드 안에 같은 문장을 되풀이하지 않는다.
 * 기본형은 「이 작업은 … 만 할 수 있습니다. 워크스페이스 소유자에게 요청해 주세요.」.
 */
export const READ_ONLY_REASON = `이 작업은 ${WRITE_ROLES_LABEL}만 할 수 있습니다. ${ROLE_REQUEST_SENTENCE}`;

/**
 * 읽기 전용 화면의 표현 규격(89차 C89-04 · UXC89-01, 90차 C90-14 · UXC90-19 로 ②를 바꿈) — 한 화면에 두 곳만 쓰고 문장은 한 번만 낸다.
 * ① 히어로 `#metaLead` 칩 하나: `READ_ONLY_CHIP_LABEL`(neutral).
 * ② **칩 아래** `#metaNote` 슬롯 한 줄(히어로 안 보조 글자): `readOnlyReasonFor(…)` 또는 `READ_ONLY_REASON`.
 *    (89차는 「히어로 바로 아래 제목 없는 neutral 상자」 였다 — 히어로 · 상자 · 구획 줄이 띠 셋으로 쌓여 첫 카드가 밀렸다. 이제 상자를 따로 두지 않는다.)
 * 상자 제목(「조회 전용」 · 「읽기 전용」)을 달지 않는다 — 칩이 이미 말하고, 같은 정보는 한 화면에 한 번이다.
 */
export const READ_ONLY_CHIP_LABEL = "권한 조회 전용";

/**
 * 히어로 칩 글을 역할로 조립한다(89차 C89-04b · RVFC89-02). 상수 하나는 면접관에게도 「권한 조회 전용」 이라 말해, 실제 권한(면접관)과 어긋나고
 * `WorkspacePage` 칩(「권한 면접관 · 조회 전용」)과도 달랐다.
 * - 조회 전용(VIEWER) → 「권한 조회 전용」. 직무 · 역량 검토자 담당이 붙어도 운영 화면에서는 같다(검토 응답은 조직 · 직무 화면의 일이다).
 * - 쓰기가 막힌 그 밖의 역할(면접관) → 「권한 면접관 · 조회 전용」.
 * - 역할을 모르면(빈 값) 상수 그대로. 조직 4화면의 검토자 칩(「권한 검토자 · 동의 · 수정 요청만」)은 `OrgFitCommandBand` 가 따로 말한다.
 */
export function readOnlyChipLabel(
  role: WorkspaceRole | "" | undefined,
): string {
  if (!role || role === "VIEWER") return READ_ONLY_CHIP_LABEL;
  return `권한 ${WORKSPACE_ROLE_LABELS[role]} · 조회 전용`;
}

/** 받침이 있으면 「은」, 없으면 「는」(한글 마지막 글자 기준 — 한글이 아니면 「은(는)」). */
function topicParticle(word: string): string {
  const code = word.trimEnd().charCodeAt(word.trimEnd().length - 1) - 0xac00;
  if (code < 0 || code > 11171) {
    return "은(는)";
  }
  return code % 28 === 0 ? "는" : "은";
}

/**
 * 「내용은 볼 수 있지만 … 은 … 만 할 수 있습니다.」 형 문장 — 화면이 막힌 동작 목록(`actions`)만 넘긴다.
 * 예: `readOnlyReasonFor("서류 항목 저장 · 발송 · 제출 관리")`.
 */
export function readOnlyReasonFor(actions: string): string {
  return `내용은 볼 수 있지만 ${actions}${topicParticle(actions)} ${WRITE_ROLES_LABEL}만 할 수 있습니다. ${ROLE_REQUEST_SENTENCE}`;
}
